import { useState, useEffect } from 'react';
import { lsGet, lsSet } from '../lib/storage';

const J  = { fontFamily: "'Josefin Sans', sans-serif" };
const CG = { fontFamily: "'Libre Baskerville', Georgia, serif" };

const STEPS = [
  {
    icon: '◆',
    iconColor: 'var(--gold)',
    title: 'Welcome to Al-Dīwān',
    body: 'A quick tour of the four main tabs — under a minute. Tap ? on any screen to replay the guide for that screen.',
  },
  {
    icon: '🏠',
    title: 'Home',
    body: "Today's Bayt gives you a new verse every day — tap it to open the full poem. Your Path shows reading tracks matched to your level and goals.",
  },
  {
    icon: '📖',
    title: 'Reading a Poem',
    body: 'Every poem has three tabs: READ to follow the verse (tap any word for its definition), STUDY for word-by-word breakdown, REFERENCE for the full poem at once.',
  },
  {
    icon: '🗺️',
    title: 'Browse',
    body: 'The Browse tab shows all 9 classical themes — Wisdom, Love, Sufi, Heroism and more. Filter by era or difficulty, or search by poet name to find exactly what you want.',
  },
  {
    icon: '🔖',
    title: 'Saved',
    body: 'The Saved tab keeps your bookmarked poems and full reading history. Tap any poem to pick up exactly where you left off.',
  },
  {
    icon: '🗂️',
    title: 'Words & Flashcards',
    body: 'Every word you save across any poem lives in the Words tab. Review them as spaced-repetition flashcards — due cards refresh daily.',
  },
];

export default function TourOverlay({ onVisibilityChange }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (lsGet('diwanTourSeenV3')) return;
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  // Permanently dismiss — only called by explicit button press
  function dismiss() {
    lsSet('diwanTourSeenV3', '1');
    setVisible(false);
  }

  // Close for this session only — tour reappears on next visit
  function closeForNow() {
    setVisible(false);
  }

  function next() {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else dismiss();
  }

  function back() {
    setStep(s => s - 1);
  }

  if (!visible) return null;

  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.93)',
        display: 'flex', alignItems: 'flex-end',
        paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        animation: 'fadeIn 0.2s ease',
      }}
      onClick={closeForNow}
    >
      <div
        style={{
          width: '100%', maxWidth: 480, margin: '0 auto',
          background: 'var(--bg)',
          borderRadius: '18px 18px 0 0',
          animation: 'slideUp 0.3s ease',
          boxShadow: '0 -6px 40px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100svh - 64px - env(safe-area-inset-bottom, 0px) - 8px)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 1.5rem 0' }}>
          {/* Handle bar */}
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 1.25rem' }} />

          {/* Step dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: '1.5rem' }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                height: 5, borderRadius: 3,
                width: i === step ? 20 : 5,
                background: i === step ? 'var(--gold)' : i < step ? 'rgba(229,150,83,0.35)' : 'var(--border)',
                transition: 'all 0.25s ease',
              }} />
            ))}
          </div>

          {/* Icon */}
          <div style={{ textAlign: 'center', fontSize: '2.2rem', marginBottom: '0.9rem', lineHeight: 1, color: s.iconColor }}>
            {s.icon}
          </div>

          {/* Title */}
          <div style={{ ...CG, fontSize: '1.15rem', fontWeight: 500, color: 'var(--text)', textAlign: 'center', marginBottom: '0.65rem' }}>
            {s.title}
          </div>

          {/* Body */}
          <div style={{ ...CG, fontSize: '0.88rem', color: 'var(--text2)', lineHeight: 1.75, textAlign: 'center', marginBottom: '1rem', padding: '0 0.5rem' }}>
            {s.body}
          </div>

          {/* ? button callout */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            background: 'rgba(229,150,83,0.07)', border: '1px solid rgba(229,150,83,0.22)',
            borderRadius: 8, padding: '0.55rem 0.8rem', marginBottom: '1rem',
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              border: '1px solid rgba(229,150,83,0.5)', background: 'rgba(229,150,83,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...J, fontSize: '0.65rem', color: 'var(--gold)', fontWeight: 600,
            }}>?</div>
            <span style={{ ...J, fontSize: '0.52rem', letterSpacing: '0.08em', color: 'var(--text3)', lineHeight: 1.5 }}>
              Tap <strong style={{ color: 'var(--gold)' }}>?</strong> on any screen whenever you need help — it replays the guide for that page.
            </span>
          </div>
        </div>

        {/* Sticky buttons footer */}
        <div style={{
          padding: '0.75rem 1.5rem 1.25rem',
          flexShrink: 0,
          borderTop: '1px solid var(--border)',
          background: 'var(--bg)',
        }}>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            {step === 0 ? (
              <button
                onClick={dismiss}
                style={{
                  ...J, flex: 1, padding: '0.8rem',
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 8, fontSize: '0.58rem', letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer',
                }}
              >
                Skip
              </button>
            ) : (
              <button
                onClick={back}
                style={{
                  ...J, flex: 1, padding: '0.8rem',
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 8, fontSize: '0.58rem', letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer',
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={next}
              style={{
                ...J, flex: 2, padding: '0.8rem',
                background: 'var(--gold)', border: 'none',
                borderRadius: 8, fontSize: '0.58rem', letterSpacing: '0.14em',
                textTransform: 'uppercase', color: '#1a1409', cursor: 'pointer', fontWeight: 700,
              }}
            >
              {isLast ? 'Get Started' : `Next — ${step + 1} of ${STEPS.length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
